import React, { useEffect, useState, useRef } from "react";
import { Link } from "@/components/common/Link";
import { Star } from "lucide-react";
const stats = [{
  id: 1,
  count: 3799,
  suffix: "+",
  label: "HOMES SERVICED",
  sublabel: "across greater Sydney",
  separator: true
}, {
  id: 2,
  count: 50,
  divider: 10,
  decimals: 1,
  suffix: "",
  label: "GOOGLE RATING",
  sublabel: "from verified reviews"
}, {
  id: 3,
  count: undefined,
  value: "Same Day",
  label: "RESPONSE TIME",
  sublabel: "for most Sydney suburbs"
}, {
  id: 4,
  count: 100,
  suffix: "%",
  label: "SATISFACTION RATE",
  sublabel: "warranty on all work"
}];
function Counter({
  target,
  suffix = "",
  divider = 1,
  decimals = 0,
  separator = false,
  isVisible
}: {
  target: number;
  suffix?: string;
  divider?: number;
  decimals?: number;
  separator?: boolean;
  isVisible: boolean;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const duration = 1800;
    const startTime = performance.now();
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * (target / divider);
      setCount(current);
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, target, divider]);
  const displayValue = count.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  return <>
      {displayValue}
      <span className="text-[#F5A623]" aria-hidden="true">{suffix}</span>
    </>;
}
export default function TrustCredibilityStrip() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, {
      threshold: 0.2
    });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  return <section id="trust-credibility-strip" ref={sectionRef} className="relative bg-[linear-gradient(135deg,#081438_0%,#0d1e3a_60%,#00285a_100%)] pt-[60px] pb-[80px] overflow-hidden !text-[rgba(28,31,34,1)] md:pt-[80px] md:pb-[100px]">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-[linear-gradient(90deg,transparent_5%,#F5A623_30%,#ffc142_50%,#F5A623_70%,transparent_95%)] z-10" />

      <div className="absolute -top-[50px] -right-[50px] w-[300px] h-[300px] bg-[radial-gradient(circle,rgba(245,166,35,0.04)_0%,transparent_70%)] pointer-events-none z-0 hidden md:block" />
      <div className="absolute top-0 right-0 bottom-0 w-full md:w-[55%] bg-[radial-gradient(rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:24px_24px] pointer-events-none z-0" />

      <div className="relative z-10 container mx-auto px-8 max-w-[1200px]">
        <div className="grid grid-cols-1 lg:grid-cols-[45%_55%] gap-12 lg:gap-16 items-center">
          
          <div className={`transition-all duration-700 ease-out delay-200 text-center lg:text-left ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-[30px]'}`}>
            <div className="w-10 h-[2px] bg-[#F5A623] mb-3 mx-auto lg:ml-0 rounded-md" aria-hidden="true" />
            <span className="font-['Manrope'] text-[11px] font-semibold uppercase tracking-[0.12em] text-[#F5A623] mb-5 block">
              BUILT ON TRUST
            </span>
            <h2 className="font-['Manrope'] text-[28px] md:text-[40px] font-extrabold text-white leading-[1.15] tracking-[-0.02em] mb-5">
              Sydney Relies on Gold Garage Door
            </h2>
            <p className="font-['Manrope'] text-[16px] text-white/55 leading-[1.7] max-w-[420px] mx-auto lg:ml-0 mb-8">
              From broken springs to full installations, our technicians have serviced thousands of homes across every Sydney suburb — with the same commitment to quality every single time.
            </p>
            <Link to="#" className="group relative inline-flex items-center gap-1.5 font-['Manrope'] text-[14px] font-semibold text-[#F5A623] transition-all duration-200">
              See our latest projects 
              <span className="transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true">→</span>
              <div className="absolute bottom-0 left-0 w-0 h-[1px] bg-[#F5A623] transition-all duration-300 group-hover:w-full" aria-hidden="true" />
            </Link>
          </div>

          <div className="relative">
            <div className={`grid grid-cols-2 grid-rows-2 relative ${isVisible ? 'visible' : ''}`}>
              <div className={`absolute top-0 bottom-0 left-1/2 w-[1px] bg-[#F5A623]/15 origin-center transition-transform duration-800 delay-500 ${isVisible ? 'scale-y-100' : 'scale-y-0'}`} />
              <div className={`absolute left-0 right-0 top-1/2 h-[1px] bg-[#F5A623]/15 origin-center transition-transform duration-800 delay-500 ${isVisible ? 'scale-x-100' : 'scale-x-0'}`} />
              
              <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-[#F5A623]/25 z-20 transition-opacity duration-400 delay-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`} aria-hidden="true" />

              {stats.map((stat, i) => <div key={stat.id} data-index={i} className={`p-6 md:p-9 transition-all duration-600 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[20px]'}`} style={{
              transitionDelay: `${300 + i * 150}ms`
            }}>
                  <div className="font-['Manrope'] text-[36px] md:text-[48px] font-extrabold text-white leading-none flex items-center gap-2">
                    {i === 1 && <div className="inline-flex items-center gap-2">
                        <Star className="w-6 h-6 fill-[#F5A623] text-[#F5A623]" />
                        <span className="text-white text-[18px] md:text-[22px] font-bold">Excellent on Google</span>
                        <span className="text-white text-[18px] md:text-[22px] font-bold">5.0</span>
                        <span className="text-white/60 text-[14px] font-medium">from 275 reviews</span>
                      </div>}
                    {stat.count !== undefined ? i !== 1 && <Counter target={stat.count} suffix={stat.suffix} divider={stat.divider} decimals={stat.decimals} separator={stat.separator} isVisible={isVisible} /> : <span className="text-[32px] md:text-[40px]">{stat.value}</span>}
                  </div>
                  <div className="font-['Manrope'] text-[12px] font-semibold uppercase tracking-[0.06em] text-white/35 mt-2.5">
                    {stat.label}
                  </div>
                  <div className="font-['Manrope'] text-[13px] font-normal text-white/20 mt-1">
                    {stat.sublabel}
                  </div>
                </div>)}
            </div>
          </div>

        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full h-[40px] md:h-[60px] z-10">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="w-full h-full block" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          <polygon points="0,60 1440,60 1440,20 0,60" fill="#FFFFFF" />
        </svg>
      </div>
    </section>;
}